/*--------------------------------------------------------------
 * Copyright (c) Microsoft Corporation. All rights reserved.
 * See License.txt in the project root for license information.
 *--------------------------------------------------------------*/

import { Module, Node } from '@msdyn365-commerce-modules/utilities';
import * as React from 'react';
import { INodeProps } from '@msdyn365-commerce-modules/utilities';
import { IHelp, IOrderSummary, IPaymentMethods, IPriceContext } from '@msdyn365-commerce-modules/order-management/src/modules/order-confirmation/../../common';
import { IGroup, IGroupDelivery, IGroups } from '@msdyn365-commerce-modules/order-management/src/modules/order-confirmation/../../common/get-groups';
import { IOrderInformation } from '@msdyn365-commerce-modules/order-management/src/modules/order-confirmation/../../common/get-order-information';
import { ISalesLine } from '@msdyn365-commerce-modules/order-management/src/modules/order-confirmation/../../common/get-sales-line';
import { IOrderConfirmationViewProps } from '@msdyn365-commerce-modules/order-management/src/modules/order-confirmation/./order-confirmation';
import {IOrderConfirmationProps, IOrderConfirmationResources} from '../definition-extensions/order-confirmation.ext.props.autogenerated';
// import { PriceComponent } from '@msdyn365-commerce/components';
import msdyn365Commerce from '@msdyn365-commerce/core-internal';
import { SalesOrder } from '@msdyn365-commerce/retail-proxy';


export interface IOrderSummaryR extends IOrderSummary {
    priceContext?: IPriceContext;
    backToShoppingLink? : React.ReactNode;
    resources:  IOrderConfirmationResources;
}

// interface ISummaryLineProps {
//     priceContext?: IPriceContext;
//     name: string;
//     label: string;
//     value?: number;
// }


// const OrderSummaryLine: React.FC<ISummaryLineProps> = ({ name, label, value = 0, priceContext }) => (
//     <p className={`ms-order-confirmation__order-summary-line-${name}`}>
//         <span className='ms-order-confirmation__order-summary-label'>
//             {label}
//         </span>
//         {priceContext ? (
//             <PriceComponent
//                 {...priceContext}
//                 className='ms-order-confirmation__order-summary-price'
//                 data={{ price: { CustomerContextualPrice: value } }}
//             />
//         ) : (
//             <span className='ms-order-confirmation__order-summary-price'>
//                 {value}
//             </span>
//         )}
//     </p>
// );

export interface IGroupR extends IGroup {
    resources: IOrderConfirmationResources;
}

export interface IGroupsR extends IGroups {
    groupsProps: INodeProps;
    groups: IGroup[];
    resources: IOrderConfirmationResources;
}

export const OrderConfirmationOrderInfomation: React.FC<IOrderInformation> = ({
    orderInformationProps,
    receiptEmail,
    createdDate
}) => (
    <Node {...orderInformationProps}>
        {createdDate}
        {receiptEmail}
    </Node>
);

export const OrderConfirmationSalesLine: React.FC<ISalesLine> = ({ salesLineProps, salesLine, buyAgainButton }) => (
    <Node {...salesLineProps}>
        {salesLine}
    </Node>
);

export const OrderConfirmationGroupDelivery: React.FC<IGroupDelivery> = ({ deliveryProps, heading, count, processing, address, trackingInfo, pickupDateTimeslot }) => (
    <Node {...deliveryProps}>
        {heading}
        {address}
        {pickupDateTimeslot}
    </Node>
);

export const OrderConfirmationGroup: React.FC<IGroupR> = ({ groupProps, delivery, salesLinesProps, salesLines, resources }) => (

    <Node {...groupProps}>
        {delivery && <OrderConfirmationGroupDelivery {...delivery} />}
        {/* {delivery && delivery.shippingItemsToYou} */}
        <h2 className="orderSummaryTitle">{resources.orderSummaryTitle}</h2>
        {_renderOrderSummaryHeading(resources)}
        {salesLines && (
            <Node {...salesLinesProps}>
                {salesLines.map((salesLine: ISalesLine) => (
                    <OrderConfirmationSalesLine key={salesLine.data.salesLine.LineId} {...salesLine} />
                ))}
            </Node>
        )}
    </Node>

);

export const OrderConfirmationGroups: React.FC<IGroupsR> = ({ groupsProps, groups, resources }) => (
    <Node {...groupsProps}>
        {groups.map((group: IGroup, index: number) => (
            <OrderConfirmationGroup resources={resources} key={index} {...group} />
        ))}
    </Node>
);

export interface IExtendedOrderSummaryR extends IOrderSummaryR {
    salesOrder: SalesOrder | undefined;
}

export const OrderConfirmationOrderSummary: React.FC<IExtendedOrderSummaryR> = ({ salesOrder, orderSummaryProps, heading, shipping, subtotal, totalAmount, earnedPoints, backToShoppingLink, resources}) => (
    <Node {...orderSummaryProps}>
        {heading}
        {/* <OrderSummaryLine
            name='subtotal'
            label={resources.orderSummaryItemsTotalLabelInclTax}
            value={Math.abs(salesOrder?.SubtotalAmountWithoutTax || 0)}
            priceContext={priceContext}
        /> */}

        {React.cloneElement(subtotal as React.ReactElement, {value: salesOrder?.SubtotalAmountWithoutTax})}

        {shipping}
        {/*tax*/}
        {totalAmount}
        {earnedPoints}
        
        {backToShoppingLink}
    </Node>
);

export const OrderConfirmationPayment: React.FC<IPaymentMethods> = ({ paymentMethodsProps, title, methods }) => (
    <Node {...paymentMethodsProps}>
        {title}
        {methods}
    </Node>
);

export const OrderConfirmationHelp: React.FC<IHelp> = ({ helpProps, needHelpLabel, helpLineNumberLabel, contactNumber }) => (
    <Node {...helpProps}>
        {needHelpLabel}
        {helpLineNumberLabel}
        {contactNumber}
    </Node>
);

const _renderOrderSummaryHeading = (resources: IOrderConfirmationResources & IOrderConfirmationResources): JSX.Element | null => {
    return (
        <div className='order-confirmation-order-summary-headings'>
            <div className='product-column-heading'>{resources.productColumnHeading}</div>
            <div className='color-column-heading'>{resources.colorColumnHeading}</div>
            <div className='size-column-heading'>{resources.sizeColumnHeading}</div>
            <div className='quantity-column-heading'>{resources.quantityColumnHeading}</div>
            <div className='price-column-heading'>{resources.priceColumnHeading}</div>
            <div className='total-column-heading'>{resources.totalColumnHeading}</div>
        </div>
    );
};

const OrderConfirmationView: React.FC<IOrderConfirmationViewProps & IOrderConfirmationProps<{}>> = props => {
  const {
    moduleProps,
    heading,
    backToShoppingLink,
    alert,
    loading,
    orderInfomation,
    orderSummary,
    payment,
    help,
    groups,
    resources,
    salesOrder,
    config }
  = props;
    const orderString = salesOrder?.CreatedDateTime?.toString() ?? '';
    const orderDate = new Date(orderString);

    const changeHref = () => {
        if (msdyn365Commerce.isBrowser && config.backToShoppingLink){
            let el = document.querySelectorAll<HTMLAnchorElement>('.ms-order-confirmation__btn-keep-shopping')[0];
            if (el.href !== config.backToShoppingLink){
                el.href = config.backToShoppingLink;
            }
        }
    }

    changeHref();
        
    return (
        <Module {...moduleProps}>
            {heading}
            <h1 className='channelRef'>{orderInfomation?.channelReferenceId}</h1>
            {alert}
            {loading}
            <p className='thankYouMessage'>{resources.thankYouMessage}</p>
             {/* {orderInfomation && <OrderConfirmationOrderInfomation {...orderInfomation} />} */}

            <span className="confirmationDetails">
                <span className="createdDate">
                {resources.orderPlacedOnLabel.replace(":","")}
                <span>{orderDate.toLocaleDateString('en-GB')}</span>
                </span>
                {orderInfomation?.receiptEmail}
            </span>
            {groups && <OrderConfirmationGroups resources={resources} {...groups} />}
             {/* {payment && <OrderConfirmationPayment {...payment} />} */}
            {orderSummary && <OrderConfirmationOrderSummary salesOrder={salesOrder} resources={resources} backToShoppingLink={backToShoppingLink} priceContext={getPriceContext(props)} {...orderSummary} />}
            <span className="paymentMethod">
                {resources.tyvm}{resources.paidMsg}
                {payment?.methods}
                <br />
                {resources.signOff}
                <br />
                {resources.teamNile}
            </span>
            {help && <OrderConfirmationHelp {...help} />}
        </Module>
    );
};

const getPriceContext = (props: IOrderConfirmationViewProps & IOrderConfirmationProps<{}>): IPriceContext  => {
  const {
    id,
    typeName,
    context,
    telemetry,
    resources: { freePriceText }
  } = props;
  return {
    id,
    typeName,
    context,
    telemetry,
    freePriceText
  };
}

export default OrderConfirmationView;
